import { useState, useEffect, useCallback, useRef } from "react";
import { fetchPdfBlobUrl, fileNameFromUrl } from "../../utils/pdfFiles";
import { getUserIdFromUrl, rememberUser } from "../../utils/currentUser";
import HomePage from "../home/HomePage";
import { showToast } from "../layout/Toast";
import SignIn from "../home/SignIn";

const hasCaseContext = () => {
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get("case_no") || params.get("case_year") || params.get("case_type"));
};

const getDeepLinkPdf = () => {
  const params = new URLSearchParams(window.location.search);
  const url = params.get("pdf_url") || params.get("pdf"); // Support both pdf_url and pdf
  return url && url.startsWith("http") ? { url, name: (params.get("pdf_name") || "").trim() } : null;
};

/** Shown while no PDF is open. Decides between:
 *   - a `?pdf_url=` deep link (the embedded/iframe flow) → load it straight away
 *   - no user in the URL → sign-in screen
 *   - otherwise → the user's home page (workspaces + files) */
export default function PDFSelector({ onSelect, onSelectFiles, onOpenWorkspace }) {
  const [deepLink] = useState(getDeepLinkPdf);
  const [userId] = useState(getUserIdFromUrl);
  const [deepLinkError, setDeepLinkError] = useState("");
  const lastAutoLoadedUrl = useRef(null);

  const loadPdfFromUrl = useCallback(async (url, preferredFileName = "") => {
    const blobUrl = await fetchPdfBlobUrl(url);
    return onSelect(blobUrl, preferredFileName || fileNameFromUrl(url), url);
  }, [onSelect]);

  // Deep linking support
  useEffect(() => {
    if (!deepLink || lastAutoLoadedUrl.current === deepLink.url) return;
    lastAutoLoadedUrl.current = deepLink.url;
    loadPdfFromUrl(deepLink.url, deepLink.name)
      .then(ok => { if (!ok) setDeepLinkError("Failed to load PDF"); })
      .catch(err => {
        console.error("[PDF LOAD ERROR]", err);
        showToast(`Couldn't open the linked PDF: ${err.message}`, "error", 5000);
        setDeepLinkError(err.message || "Failed to load PDF");
      });
  }, [deepLink, loadPdfFromUrl]);

  useEffect(() => { if (userId) rememberUser(userId); }, [userId]);

  if (deepLink && !deepLinkError) {
    return (
      <div className="pdfu-page">
        <div className="pdfu-loading">
          <span className="pdfu-spinner" />
          Opening document…
        </div>
      </div>
    );
  }

  // The embedded case flow identifies itself through case_* params, not a user name.
  if (!userId && !hasCaseContext()) return <SignIn />;

  return (
    <HomePage
      userId={userId || "this case"}
      onSelect={onSelect}
      onSelectFiles={onSelectFiles}
      onOpenWorkspace={onOpenWorkspace}
      onSwitchUser={() => window.location.assign(window.location.origin + "/")}
    />
  );
}
